import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { FaTimes, FaSave } from 'react-icons/fa';
import { 
  AssetWarranty, 
  UpdateWarrantyRequest,
  updateProjectAssetWarranty
} from '../../api/assets';

interface EditWarrantyModalProps {
  projectId: string;
  assetId: string;
  warranty: AssetWarranty | null;
  isOpen: boolean;
  onClose: () => void;
  onWarrantyUpdated: () => void;
}

export const EditWarrantyModal: React.FC<EditWarrantyModalProps> = ({
  projectId,
  assetId,
  warranty,
  isOpen,
  onClose,
  onWarrantyUpdated
}) => {
  const { keycloak } = useKeycloak();
  const [formData, setFormData] = useState<UpdateWarrantyRequest>({
    project_material_id: assetId,
    unit_id: null,
    manufacturer_id: '',
    warranty_start_date: '',
    warranty_end_date: '',
    warranty_provider: '',
    warranty_terms: null,
    coverage_details: null,
    serial_number: '',
    model: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  const authToken = keycloak.token;

  useEffect(() => {
    if (warranty) {
      setFormData({
        project_material_id: warranty.project_material_id,
        unit_id: warranty.unit_id,
        manufacturer_id: warranty.manufacturer_id,
        warranty_start_date: warranty.warranty_start_date ? warranty.warranty_start_date.split('T')[0] : '', // Format for date input
        warranty_end_date: warranty.warranty_end_date ? warranty.warranty_end_date.split('T')[0] : '', // Format for date input
        warranty_provider: warranty.warranty_provider,
        warranty_terms: warranty.warranty_terms,
        coverage_details: warranty.coverage_details,
        serial_number: warranty.serial_number,
        model: warranty.model,
        is_active: warranty.is_active
      });
    }
  }, [warranty]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authToken) return;

    setSaving(true);
    try {
      // Convert date strings to ISO format
      const warrantyData = {
        ...formData,
        warranty_start_date: formData.warranty_start_date ? new Date(formData.warranty_start_date).toISOString() : '',
        warranty_end_date: formData.warranty_end_date ? new Date(formData.warranty_end_date).toISOString() : ''
      };

      await updateProjectAssetWarranty(authToken, projectId, assetId, warrantyData);
      toast.success('Warranty updated successfully');
      onWarrantyUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating warranty:', error);
      toast.error('Failed to update warranty');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">
            Edit Warranty Information
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Warranty Provider *
              </label>
              <input
                type="text"
                name="warranty_provider"
                value={formData.warranty_provider}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Manufacturer ID *
              </label>
              <input
                type="text"
                name="manufacturer_id"
                value={formData.manufacturer_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Serial Number *
              </label>
              <input
                type="text"
                name="serial_number"
                value={formData.serial_number}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Model *
              </label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Warranty Start Date *
              </label>
              <input
                type="date"
                name="warranty_start_date"
                value={formData.warranty_start_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Warranty End Date *
              </label>
              <input
                type="date"
                name="warranty_end_date"
                value={formData.warranty_end_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit ID <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                name="unit_id"
                value={formData.unit_id || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Leave empty if asset will be added to rack later"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional: Assets can be assigned to racks during installation
              </p>
            </div>

            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">Active Warranty</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Warranty Terms
              </label>
              <textarea
                name="warranty_terms"
                value={formData.warranty_terms || ''}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coverage Details
              </label>
              <textarea
                name="coverage_details"
                value={formData.coverage_details || ''}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
            >
              <FaSave />
              {saving ? 'Saving...' : 'Save Warranty'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 